import React from 'react';
import { useQuery } from 'react-query';

import * as api from 'config/api';
import * as env from 'config/environment';
import * as consts from 'config/consts';

import { GetIndexesItemResult, getIndexes, getOrders } from 'config/api';
import { Frequency, reportLoadingStyle } from 'pages/NewReport';
import { formatDate } from 'utils/formatter';
import { LineNoticeBanner } from 'components/features/home/HomeContentsInternal';
import Loading from 'components/commons/loading/Loading';
import { isPremium } from 'components/features/utils/userStatus';
import { AdvisorComment } from './components/AdvisorComment';
import { AssetChange } from './components/AssetChange';
import { AssetTransitionChart } from './components/AssetTransitionChart';
import { ColumnTable } from './components/ColumnTable';
import { ManagedAssetsReport } from './components/ManagedAssetsReport';
import { ComparisonBetweenIndexAndAssetInternal as ComparisonBetweenIndexAndAsset } from './components/ComparisonBetweenIndexAndAsset';
import { AssetsFluctuation } from './components/AssetsFluctuation';
import { RealizedProfitAndLoss } from './components/RealizedProfitAndLoss';
import { TargetAchievementList } from './components/TargetAchievementList';
import { Sign } from '../utils/formatter';
import { OrderItemProps, yyyymmddToDate } from '../daily-report/DailyReport';
import { ReportBaseProps, getQueryPeriod } from '../weekly-report/WeeklyReport';
import {
  ReportResult,
  ReportResultProps,
} from '../monthly-report/MonthlyReport';

export const getSignColor = (sign: Sign) => {
  switch (sign) {
    case 'plus':
      return 'text-upcolor';
    case 'minus':
      return 'text-downcolor';
  }
  return '';
};

const NoprepareReport: React.FC<Record<string, unknown>> = () => {
  const linkDo = () => {
    window.location.href = `${env.premiumURL}?service=${
      env.serviceName
    }&callback_url=${encodeURI(`${env.appURL}/auth?callbackPath=/target`)}`;
  };

  return (
    <div className="px-4 text-bluegray-700 bg-white">
      <div className="pt-10 text-lg font-bold text-center">準備中です</div>
      <div className="pt-2 text-xs text-center leading-6">
        年次レポートはその年末に配信されます。
        <br />
        年末にレポートが作成されるまでお待ち下さい。
      </div>
      <div className="pt-6">
        <img
          src="/static/images/report/yearly-report1.png"
          alt=""
          className="max-w-80 mx-auto"
        />
      </div>
      <div className="pt-14">
        <img
          src="/static/images/report/yearly-report2.png"
          alt=""
          className="max-w-80 mx-auto"
        />
      </div>
      {!isPremium() && (
        <>
          <div className="pt-2 text-xs text-center leading-6">
            目標の進捗率表示は
            <br />
            プレミアム会員限定機能です。
          </div>
          <div
            className="mt-2 mx-4 flex flex-col items-center text-blue-700 justify-center border border-blue-700 rounded-lg shadow py-2 px-4 bg-gray-50 cursor-pointer"
            onClick={linkDo}>
            <div className="text-base font-bold">
              資産目標サポートを試してみる
            </div>
            <div className="text-sm">初回30日間無料</div>
          </div>
        </>
      )}
      <div className="pt-14 pb-8">
        <img
          src="/static/images/report/yearly-report3.png"
          alt=""
          className="max-w-80 mx-auto"
        />
      </div>
    </div>
  );
};

type YearlyReportProps = {
  userName: string;
  query: string | null;
  setQuery: (q: string, frequency: Frequency) => void;
};

type IsLinkedLineProps = {
  isLinkedLine: boolean;
};

const YearlyReportInternal = (
  props: Omit<YearlyReportProps, 'query'> &
    IsLinkedLineProps &
    ReportResultProps
) => {
  const { isLinkedLine, report, userName, setQuery } = props;

  const from = formatDate(
    yyyymmddToDate(report.Base.From.toString()),
    'yyyy/MM/dd'
  );
  const to = formatDate(
    yyyymmddToDate(report.Base.To.toString()),
    'yyyy/MM/dd'
  );

  const {
    data: indexesData,
    isFetching: indexesIsFetching,
    isRefetching: indexesIsRefetching,
  } = useQuery('yearlyIndxes', () => fetchIndexes(from, to), {
    refetchOnWindowFocus: false,
  });

  const { data: ordersData } = useQuery(
    'yearlyOrders',
    () => fetchOrders(from, to),
    {
      refetchOnWindowFocus: false,
    }
  );

  const isItemMask =
    report.Base.DiffTotalAssetAmount === null &&
    report.Base.DiffTotalAssetRate === null &&
    report.Base.PreviousTotalAssetAmount === null;

  const base_ = report.Base as ReportBaseProps;
  const queryPeriod = getQueryPeriod(base_.From, base_.To);

  const isIndexesDataLoading =
    (indexesIsFetching && indexesIsRefetching) ||
    indexesData === null ||
    indexesData === undefined;

  const isOrdersDataLoading = ordersData === null || ordersData === undefined;

  return (
    <>
      <div>
        <div className="bg-white">
          {isOrdersDataLoading ? (
            <LoaingAssetChange />
          ) : (
            <>
              <AssetChange
                {...{
                  Base: base_,
                  CreatedAt: report.CreatedAt,
                  NextQuery: report.NextQuery,
                  PreviousQuery: report.PreviousQuery,
                  setQuery,
                }}
              />
              <RealizedProfitAndLoss
                {...{
                  orders: ordersData as OrderItemProps[],
                  From: base_.From,
                  To: base_.To,
                }}
              />
            </>
          )}
          {!isItemMask && (
            <>
              <AssetsFluctuation {...{ Base: base_ }} />
              <div className="p-4 overflow-hidden">
                <AssetTransitionChart
                  {...{
                    queryPeriod,
                    PreviousTotalAssetAmount: base_.PreviousTotalAssetAmount,
                  }}
                />
                <AdvisorComment
                  {...{
                    Base: base_,
                    userName,
                  }}
                />
              </div>
            </>
          )}
          {!isLinkedLine && isPremium() && (
            <LineNoticeBanner {...{ className: ['pb-2', 'w-1/2', 'w-3/4'] }} />
          )}
        </div>
        <div>
          {isPremium() && (
            <TargetAchievementList
              {...{ from: base_.From, queryPeriod, userName }}
            />
          )}
          {isIndexesDataLoading || isOrdersDataLoading ? (
            <LoadingManagedAssetsReport />
          ) : (
            <ManagedAssetsReport
              ComparisonBetweenIndexAndAsset={() => (
                <ComparisonBetweenIndexAndAsset
                  {...{ indexes: indexesData as GetIndexesItemResult[] }}
                />
              )}
              indexes={indexesData as GetIndexesItemResult[]}
              orders={ordersData as OrderItemProps[]}
            />
          )}
          <ColumnTable
            {...{ Articles: report.Articles, userName, From: base_.From }}
          />
          <div className="pt-3 px-4 text-xs text-bluegray-500">
            ※外貨資産の前月評価額は前月末時点の参考レートを使用しています。
          </div>
        </div>
      </div>
    </>
  );
};

export const YearlyReport = (props: YearlyReportProps & IsLinkedLineProps) => {
  const query = props.query ?? undefined;
  const { data: reportData, isLoading, isRefetching } = useQuery(
    'yearlyReport',
    () => fetchReport(query),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (reportData === null || reportData === undefined)
    return <Loading style={reportLoadingStyle} loading={true} />;

  if (reportData.Year === null) return <NoprepareReport />;

  if (
    isLoading ||
    isRefetching ||
    reportData.Year.Base.From === null ||
    reportData.Year.Base.To === null ||
    reportData.Year.NextQuery === undefined ||
    reportData.Year.PreviousQuery === undefined ||
    reportData.Year.Articles === undefined ||
    reportData.Year.Articles === null ||
    reportData.Year.CreatedAt === null
  )
    return <Loading style={reportLoadingStyle} loading={true} />;

  return (
    <YearlyReportInternal
      {...{
        report: reportData.Year as ReportResult,
        isLinkedLine: props.isLinkedLine,
        userName: props.userName,
        setQuery: props.setQuery,
      }}
    />
  );
};

const LoaingAssetChange = () => {
  return (
    <div className="bg-white">
      <div
        className="flex justify-end items-center py-1 px-4 text-gray-700"
        style={{ fontSize: '13px' }}>
        <div className="flex items-center leading-4" style={{ height: '15px' }}>
          ----/--/-- --:--<span className="ml-1">作成</span>
        </div>
      </div>
      <div className="flex items-center justify-between py-3 px-2">
        <div className="flex flex-col flex-1 items-center">
          <div className="flex flex-row items-baseline font-bold">
            <div className="text-2xl">----</div>
            <div className="text-base">年</div>
          </div>
        </div>
      </div>
      <div className="px-4 text-bluegray-700">
        <div className="text-lg font-bold">----年の確定損益</div>
        <div className="text-xs text-bluegray-500">
          対象期間 ----/--/-- ~ ----/--/--
        </div>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
        <div className="pt-3 pb-5 text-xs text-bluegray-500">
          ※確定拠出年金・マネーツリー連携口座は表示されません。
          <br />
          直近の取引はこちらに反映されないことがあります。
        </div>
      </div>
    </div>
  );
};

const LoadingManagedAssetsReport = () => {
  return (
    <>
      <div className="h-22.5 flex items-end p-4 text-lg font-bold text-bluegray-700 leading-tight">
        <img
          src="/static/images/report/icon-asset.svg"
          alt=""
          className="w-4 h-4 mr-2"
        />
        あなたの資産運用レポート
      </div>
      <div className="bg-white">
        <div className="p-4 overflow-hidden">
          <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
            <img
              src="/static/images/report/icon-chart.svg"
              alt=""
              className="mr-2"
            />
            主要指標と自分の資産を比較
          </div>
          <div className="text-xs text-black">
            あなたの資産と主要指標の1年間の変動率を見比べることができます。
          </div>
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          />
        </div>
      </div>
    </>
  );
};

const fetchReport = async (query: string | undefined) => {
  return await api.getReport(consts.reportType.年次, query);
};

const fetchIndexes = async (from: string, to: string) => {
  const res_ = await getIndexes(from, to);
  const res = res_.Items.map(item => item).reverse();
  return res;
};

const fetchOrders = async (from: string, to: string) => {
  const res_ = await getOrders(consts.reportType.年次, from, to);
  const res = res_.Items.map(res => {
    return {
      brokerID: res.BrokerID,
      brokerName: res.BrokerName,
      tradeTime: res.TradeTime,
      symbol: res.Symbol,
      symbolName: res.SymbolName,
      accountType: res.AccountType,
      productType: res.ProductType,
      cashMargin: res.CashMargin,
      tradeType: res.TradeType,
      side: res.Side,
      qty: res.Qty,
      price: res.Price,
      openPrice: res.OpenPrice,
      openPriceUSD: res.OpenPriceUSD,
      profitOrLoss: res.ProfitOrLoss,
      profitOrLossUSD: res.ProfitOrLossUSD,
      currencyUnit: res.CurrencyUnit,
    };
  });
  return res;
};
